/**
 * Utility functions for handling iOS safe area insets
 */

// Detect if we're running on iOS
export const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
         // Check for iPad on iOS 13+ (shows as Mac in user agent)
         (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
};

// Detect if we're running in standalone/PWA mode
export const isPWA = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.matchMedia('(display-mode: fullscreen)').matches ||
         // @ts-expect-error - checking for iOS PWA standalone property
         window.navigator.standalone === true;
};

// Get safe area class names for different scenarios
export const getSafeAreaClasses = (type: 'dialog' | 'sheet' | 'fullscreen' = 'dialog') => {
  const classes: string[] = [];
  
  if (isIOS()) {
    switch (type) {
      case 'dialog':
        classes.push('mt-safe-top', 'mb-safe-bottom');
        break;
      case 'sheet':
        classes.push('pt-safe-top', 'pb-safe-bottom');
        break;
      case 'fullscreen':
        classes.push('ios-fullscreen-modal');
        break;
    }
  }
  
  return classes.join(' ');
};

// Get safe area classes for headers
export const getSafeAreaHeaderClasses = () => {
  if (isIOS()) {
    return 'mt-safe-top pt-2';
  }
  return 'pt-2';
};

// Get safe area classes for footers
export const getSafeAreaFooterClasses = () => {
  if (isIOS()) {
    return 'mb-safe-bottom pb-2';
  }
  return 'pb-2';
};

// Get safe area classes for close buttons
export const getSafeAreaCloseButtonClasses = () => {
  if (isIOS()) {
    return 'right-[calc(0.5rem+env(safe-area-inset-right))] top-[calc(0.5rem+env(safe-area-inset-top))] sm:right-[calc(1rem+env(safe-area-inset-right))] sm:top-[calc(1rem+env(safe-area-inset-top))]';
  }
  return 'right-2 top-2 sm:right-4 sm:top-4';
};

// Combine with existing class names safely
export const combineSafeAreaClasses = (existingClasses: string, safeAreaClasses: string) => {
  const existing = existingClasses.split(' ').filter(Boolean);
  const safeArea = safeAreaClasses.split(' ').filter(Boolean);
  
  // Remove conflicting classes
  const filtered = existing.filter(cls => !safeArea.some(safeClass => 
    cls.includes('mt-') && safeClass.includes('mt-') ||
    cls.includes('mb-') && safeClass.includes('mb-') ||
    cls.includes('pt-') && safeClass.includes('pt-') ||
    cls.includes('pb-') && safeClass.includes('pb-') ||
    cls.includes('top-') && safeClass.includes('top-') ||
    cls.includes('right-') && safeClass.includes('right-')
  ));
  
  return [...filtered, ...safeArea].join(' ');
};
